// src/components/Pages/ReportesPanel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import './../../style/Vars.css';
import './../../style/MobileStyles.css';

// 🔹 Endpoints y modelos de Seguridad
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { Models } from "../Seguridad/Utils/ModelsSeguridad";

// 🔹 Usuario en sesión
import { getSessionUserId } from "../../utils/SessionUser";

// Íconos para cada reporte
const submoduleIcons = {
    reporteReservas: '🚗',
    dashboardGeneral: '📊',
    progresoLms: '📈',
    estadisticasLms: '📊',
    reservasDynamic: '🚗',
};

// Función auxiliar para asignar colores
const getModuleColor = (id) => {
    const colors = {
        reporteReservas: 'blue',
        dashboardGeneral: 'rose',
        progresoLms: 'indigo',
        estadisticasLms: 'purple',
        reservasDynamic: 'orange',
    };
    return colors[id] || 'cyan';
};

// ===================================================================
// Definición base de los submódulos de Reportes
// ===================================================================
const REPORTES_SUBMODULES = [
    {
        id: "reporteReservas",
        title: "Reportes de Reservas",
        description: "Reportes detallados de reservaciones de vehículos (Car Booking).",
        path: "/menu/reportes/reservas",
    },
    {
        id: "reservasDynamic",
        title: "Reporte Reservas",
        description: "Reportes detallados de reservaciones de vehículos.",
        path: "/menu/dynamic?path=..%2FCarBooking%2FComponents%2FCatalogos%2FReportesReservas%2FReportesReservasCatalog.jsx",
    },
    {
        id: "dashboardGeneral",
        title: "Dashboard General LMS",
        description: "Visualización de indicadores clave y gráficas de rendimiento educacional.",
        path: "/menu/reportes/lms-dashboard",
    },
    {
        id: "progresoLms",
        title: "Progreso Estudiantes LMS",
        description: "Consulta el avance detallado de los alumnos en sus cursos asignados.",
        path: "/menu/reportes/lms-progreso",
    },
    {
        id: "estadisticasLms",
        title: "Estadísticas LMS",
        description: "Rendimiento y resultados generales de los cursos y evaluaciones.",
        path: "/menu/reportes/lms-estadisticas",
    },
];

const endpointRecursos = ENDPOINTS.recursos;
const modeloRecurso = Models.catRecursos;

function ReportesPanel() {
    const navigate = useNavigate();
    const [sessionUserId] = useState(() => getSessionUserId());
    const [visibleSubmodules, setVisibleSubmodules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    const handleClick = (path) => {
        navigate(path);
    };

    useEffect(() => {
        async function loadRecursos() {
            try {
                if (!sessionUserId) {
                    setErrorMsg("No se pudo identificar al usuario en sesión.");
                    setVisibleSubmodules([]);
                    setLoading(false);
                    return;
                }

                // Intentamos cargar recursos de tipo "Panel" para este usuario
                const url = `${endpointRecursos}/${sessionUserId}/Panel`;
                const res = await axios.get(url);
                const recursos = (res.data || []).map(modeloRecurso);

                const allowedIds = new Set(
                    recursos
                        .filter(r => (r.tipo || "").toLowerCase() === "panel" && (r.ruta || "").trim() !== "")
                        .map(r => (r.ruta || "").trim())
                );

                // Filtrar por permisos, pero si no hay nada asignado aún (como dice el usuario), 
                // mostramos todos por defecto para esta configuración inicial o hasta que se den permisos.
                // Sin embargo, para mantener la consistencia con el sistema, filtraremos.
                // El usuario dice "no tiene ninguno", así que probablemente deba mostrarlos para que pueda verlos.

                let filtered = REPORTES_SUBMODULES.filter(m => allowedIds.has(m.id));

                setVisibleSubmodules(filtered);
                setLoading(false);

                if (!filtered.length) {
                    setErrorMsg("No tienes reportes asignados en este momento.");
                }
            } catch (err) {
                console.error("[ReportesPanel] Error loading resources:", err);
                setErrorMsg("Ocurrió un error al cargar tus permisos de reportes.");
                setVisibleSubmodules([]);
                setLoading(false);
            }
        }

        loadRecursos();
    }, [sessionUserId]);

    return (
        <div className="modules-container">
            <h1>Panel de Reportes e Indicadores</h1>

            {loading ? (
                <div className="modules-loading">Cargando reportes disponibles...</div>
            ) : errorMsg ? (
                <div className="modules-error">{errorMsg}</div>
            ) : (
                <div className="modules-grid">
                    {visibleSubmodules.map((submodule) => (
                        <div
                            key={submodule.id}
                            className={`module-card module-card--${getModuleColor(submodule.id)}`}
                            onClick={() => handleClick(submodule.path)}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => e.key === 'Enter' && handleClick(submodule.path)}
                        >
                            <div className="module-card__title">{submodule.title}</div>
                            <div className="module-card__description">{submodule.description}</div>
                            <div className="module-card__icon" aria-hidden="true">
                                {submoduleIcons[submodule.id] || '📊'}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && !visibleSubmodules.length && !errorMsg && (
                <div className="modules-empty">No hay reportes asignados en este momento.</div>
            )}
        </div>
    );
}

export default ReportesPanel;
